import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Lock } from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import StatusBadge from '@/components/StatusBadge'
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatCurrency, formatDate } from '@/lib/utils'

interface Props {
  purchase_order: PurchaseOrder
}

export default function PurchaseOrderEdit({ purchase_order: po }: Props) {
  const isFinalized = po.status === 'finalized'

  const [form, setForm] = useState({
    amount: po.amount,
    date: po.date,
    status: po.status,
    google_sheet_url: po.google_sheet_url || '',
    notes: po.notes || '',
  })
  const [processing, setProcessing] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.put(`/purchase_orders/${po.id}`, { purchase_order: form }, {
      onFinish: () => setProcessing(false),
    })
  }

  if (isFinalized) {
    return (
      <AppLayout>
        <div className="flex items-center gap-4 mb-8">
          <Button asChild variant="ghost" size="icon-sm">
            <Link href={`/purchase_orders/${po.id}`}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Purchase Order #{po.id}
              </h1>
              <StatusBadge status={po.status} />
            </div>
          </div>
        </div>

        <Card className="max-w-2xl border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-6 rounded-lg bg-warning/5 border border-warning/20 px-4 py-3">
              <Lock className="size-4 text-warning shrink-0" />
              <p className="text-sm text-warning">
                This purchase order has been finalized and cannot be edited.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Amount</p>
                <p className="text-lg font-mono font-semibold">{formatCurrency(parseFloat(po.amount))}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Date</p>
                <p className="text-sm">{formatDate(po.date)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                <StatusBadge status={po.status} />
              </div>
              {po.google_sheet_url && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Google Sheet URL</p>
                  <a
                    href={po.google_sheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {po.google_sheet_url}
                  </a>
                </div>
              )}
              {po.notes && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{po.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <Button asChild variant="outline">
                <Link href={`/purchase_orders/${po.id}`}>Back to Details</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="flex items-center gap-4 mb-8">
        <Button asChild variant="ghost" size="icon-sm">
          <Link href={`/purchase_orders/${po.id}`}>
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Edit Purchase Order #{po.id}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Update purchase order details
          </p>
        </div>
      </div>

      <Card className="max-w-2xl border-border/50">
        <CardHeader>
          <CardTitle className="text-base">Purchase Order Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => setForm({ ...form, status: value as PurchaseOrderStatus })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="estimated">Estimated</SelectItem>
                    <SelectItem value="finalized">Finalized</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="google_sheet_url">Google Sheet URL</Label>
              <Input
                id="google_sheet_url"
                type="url"
                placeholder="https://docs.google.com/spreadsheets/..."
                value={form.google_sheet_url}
                onChange={(e) => setForm({ ...form, google_sheet_url: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any relevant notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button asChild variant="ghost">
                <Link href={`/purchase_orders/${po.id}`}>Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AppLayout>
  )
}
